import {
  TravelDocumentSalesModel,
  useCreateTravelDocumentSales,
  useGetTravelDocumentSales,
  useUploadTravelDocumentSales,
} from 'api-hooks/sale/travel-document';
import { Notification, KIND } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import LoadingComponent from 'components/common/loading';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import useApprovalableEdit from 'hooks/use-approvalable-edit';
import useDismiss from 'hooks/use-dismiss';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import { DetailContainer, SeparatorVertical } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { NavigationProps } from 'types';

import { DocumentSaleFormType } from './components/form-type';
import TravelDocumentInvoiceForm from './forms/invoice-form';
import TravelDocumentUploadForm from './forms/upload-form';

export type TravelDocumentSalesViewParams = {
  id: string;
  defaultValues?: DocumentSaleFormType;
  travelDocument?: TravelDocumentSalesModel;
  invoiceNumber?: string;
};

export default function TravelDocumentSalesView(
  props: NavigationProps<NavigationRoutes.TravelDocumentSalesView>,
) {
  const { query } = useRouter();
  const id = query?.id as string;
  const [activeKey, setActiveKey] = React.useState<React.Key>('0');
  const { t } = useTranslation();
  const { data, error, isLoading, isFetching, refetch } =
    useGetTravelDocumentSales({
      id,
    });

  const { mutateAsync: updateMutate, error: updateError } =
    useCreateTravelDocumentSales();

  const { mutateAsync: uploadMutate, error: uploadError } =
    useUploadTravelDocumentSales();

  const travelDocument = data?.data;

  const onResolve = useApprovalableEdit('document-sales');
  const permission = useGetCrudPermission('document-sales');
  const { navigate } = useNavigation();
  const onDismiss = useDismiss('document-sales', travelDocument?.id);

  const invoiceForm = (
    <TravelDocumentInvoiceForm
      {...props}
      renderError={
        !!updateError &&
        !!updateError.message && (
          <>
            <Notification
              overrides={{
                Body: { style: { width: 'auto', wordBreak: 'break-all' } },
              }}
              kind={KIND.negative}
            >
              {() => updateError.message}
            </Notification>
            <SeparatorVertical $margin={8} />
          </>
        )
      }
      travelDocument={travelDocument}
      onSubmit={async (input, form) => {
        const result = await updateMutate(input);
        onResolve(result);
        return result.data;
      }}
    />
  );

  const documentForm = (
    <TravelDocumentUploadForm
      {...props}
      renderError={
        !!uploadError &&
        !!uploadError.message && (
          <>
            <Notification
              overrides={{
                Body: { style: { width: 'auto', wordBreak: 'break-all' } },
              }}
              kind={KIND.negative}
              closeable
            >
              {() => uploadError.message}
            </Notification>
            <SeparatorVertical $margin={8} />
          </>
        )
      }
      travelDocument={travelDocument!}
      onSubmit={async (input, form) => {
        const result = await uploadMutate({
          body: input,
          id: travelDocument!.id,
        });

        onDismiss(travelDocument!.id);

        notification.success({ message: result?.message });

        if (permission.view) {
          navigate(NavigationRoutes.TravelDocumentSalesView, {
            id: travelDocument!.id,
          });
        }

        return result;
      }}
    />
  );

  return (
    <FetchWrapperComponent
      onRetry={refetch}
      loadingComponent={<LoadingComponent />}
      error={error}
      errorComponent={
        <DetailContainer>
          <ErrorViewComponent refetch={refetch} />
        </DetailContainer>
      }
      isLoading={isLoading || isFetching}
      component={
        <>
          <Tabs
            activeKey={activeKey}
            items={[
              {
                title: t('common:detail'),
                component: <></>,
              },
              {
                title: t('common:document'),
                component: <></>,
              },
            ]}
            onChangeActive={({ activeKey }) => setActiveKey(activeKey)}
            overrideStyles={{
              tabList: { minHeight: '0px' },
              tab: {
                minHeight: '0px',
                height: '0',
                flexGrow: '0',
                zIndex: 1,
              },
              tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
            }}
          />
          <div
            style={{
              display: activeKey === '0' ? 'block' : 'none',
              overflow: 'auto',
              position: 'absolute',
              inset: 0,
              top: 70,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            {invoiceForm}
          </div>
          <div
            style={{
              display: activeKey === '1' ? 'block' : 'none',
              overflow: 'auto',
              position: 'absolute',
              inset: 0,
              top: 70,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <Separator gap={17} />
            {documentForm}
          </div>
        </>
      }
    />
  );
}
